'use client';

import { useSocketStore } from '@/lib/services/socket/unified-socket-service';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';

interface UnifiedSocketProviderProps {
  children: React.ReactNode;
  showConnectionTest?: boolean;
}

/**
 * UnifiedSocketProvider
 *
 * A provider component that initializes the socket connection and provides
 * socket context to the application.
 *
 * @param children The child components
 * @param showConnectionTest Whether to show the connection test component (no longer used)
 */
export default function UnifiedSocketProvider({
  children,
  // Keep parameter for backward compatibility but ignore it
  showConnectionTest = false,
}: UnifiedSocketProviderProps) {
  const { initializeSocket, isConnected, error } = useSocketStore();
  const { data: session } = useSession();
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);

  // Seed the socket store with real session identity before connecting.
  // Without this, the handshake sends random user-XYZ values and no
  // `role`, so `joinDefaultRooms` on the server can't place admins into
  // the `admins` broadcast room used by the Alerts subsystem.
  useEffect(() => {
    if (!session?.user) return;
    useSocketStore.setState({
      userId: session.user.id,
      userName: session.user.name ?? `User ${session.user.id.slice(0, 6)}`,
      role: session.user.role
    });
  }, [session?.user]);

  // Initialize the socket when the component mounts with retry logic
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    const connectWithRetry = async () => {
      if (!mounted) return;

      try {
        setIsInitializing(true);
        await initializeSocket();

        // Set a timeout to consider initialization complete even if connection fails
        timeoutId = setTimeout(() => {
          if (mounted) {
            setIsInitializing(false);
          }
        }, 3000); // 3 second timeout for initial connection attempt
      } catch (err) {
        console.error('Socket initialization error:', err);

        // Retry with exponential backoff if we're still mounted
        if (mounted) {
          const nextAttempt = connectionAttempts + 1;
          setConnectionAttempts(nextAttempt);

          // Calculate backoff time (max 10 seconds)
          const backoff = Math.min(Math.pow(1.5, nextAttempt) * 1000, 10000);

          console.log(`Retrying socket connection in ${backoff}ms (attempt ${nextAttempt})`);

          timeoutId = setTimeout(() => {
            if (mounted) {
              connectWithRetry();
            }
          }, backoff);
        }
      }
    };

    connectWithRetry();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [initializeSocket, connectionAttempts]);

  // Mark initialization as complete after a maximum wait time
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsInitializing(false);
    }, 5000); // 5 second maximum wait time

    return () => clearTimeout(timeoutId);
  }, []);

  // Once connected, reset connection attempts
  useEffect(() => {
    if (isConnected) {
      setConnectionAttempts(0);
      setIsInitializing(false);
    }
  }, [isConnected]);

  // Render children even if socket is not connected after initialization period
  return (
    <>
      {(!isInitializing || !error) && children}
    </>
  );
}
